import { faInfoCircle, faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Arrange, Button, Link, NewBox, Spacer, Typography, useToast } from "@aviary";
import { WellevateByFullscript } from "@shared/assets/logo";
import FullscriptLogoSvg from "@shared/assets/logo/fullscript-logo.svg";
import { EmailInput } from "@shared/components";
import { AnalyticsEvent, useAnalyticsLogger } from "@shared/hooks";
import { FontAwesomeIcon } from "@shared/react-fontawesome";
import { useSearchParams } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import { BoxLink, PasswordInput } from "@unauthenticated/shared/components";
import { l } from "@unauthenticated/wellevateAuthorize/practitioners/locales/i18n";
import { AccountCreationStep } from "@unauthenticated/wellevateAuthorize/practitioners/types";
import type { WellevateSignupFormData } from "@unauthenticated/wellevateAuthorize/practitioners/types";

import type { AuthenticateWellevatePractitionerData } from "./AuthenticateWellevatePractitioner.mutation";
import { useAuthenticateWellevatePractitionerMutation } from "./AuthenticateWellevatePractitioner.mutation";

import * as styles from "./WellevateAuthenticationPage.styles";

interface Props {
  setFormData: Dispatch<SetStateAction<WellevateSignupFormData>>;
}

const WellevateAuthenticationPage = ({ setFormData }: Props) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { makeToast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);
  const [areCredentialsIncorrect, setAreCredentialsIncorrect] = useState(false);
  const logAnalyticsEvent = useAnalyticsLogger();

  const handleEmailOnChange = e => {
    setEmail(e.target.value);
    setAreCredentialsIncorrect(false);
  };

  const handleEmailOnBlur = e => {
    if (e.target.value) setIsEmailDirty(true);
  };

  const handlePasswordOnChange = e => {
    setPassword(e.target.value);
    setAreCredentialsIncorrect(false);
  };

  const handlePasswordOnBlur = e => {
    if (e.target.value) setIsPasswordDirty(true);
  };

  const getPasswordErrors = () => {
    if (areCredentialsIncorrect) {
      return [t(l.common.WellevateAuthenticationPage.incorrectEmailorPassword)];
    }
    if (!isPasswordDirty) return [];
    if (!password.length) return [t(l.common.WellevateAuthenticationPage.passwordIsRequired)];
  };

  const handleError = (errorMessage: string) => {
    if (errorMessage === "ALREADY_CONNECTED") {
      setFormData(previousData => ({ ...previousData, email }));
      setSearchParams({ step: AccountCreationStep.ACCOUNT_ERROR });
    } else {
      makeToast("error", errorMessage);
    }
  };

  const handleAuthOnCompleted = (data: AuthenticateWellevatePractitionerData) => {
    if (data.auth?.authenticateWellevatePractitioner?.errors?.message) {
      handleError(data.auth?.authenticateWellevatePractitioner?.errors?.message);
    } else {
      const practitioner = data.auth?.authenticateWellevatePractitioner;

      setFormData({
        email: practitioner.email,
        firstName: practitioner.firstname,
        lastName: practitioner.lastname,
        dispensaryName: practitioner.dispensaryName,
        practitionerTypeId: practitioner.fullscriptPractitionerType?.id ?? "",
        practitionerType: practitioner.fullscriptPractitionerType?.description ?? "",
        wellevatePractitionerId: practitioner.practitionerId,
        tosTimestamp: "",
      });
      setSearchParams({ step: AccountCreationStep.ACCOUNT_DETAILS });
    }
  };

  const [authenticateWellevatePractitioner, { loading: authLoading }] =
    useAuthenticateWellevatePractitionerMutation({
      onCompleted: data => handleAuthOnCompleted(data),
      onError: ({ message: errorMessage }) => handleError(errorMessage),
    });
  const handleWVSignIn = () => {
    if (!email.length) return setIsEmailDirty(true);
    if (!password.length) return setIsPasswordDirty(true);

    gRecaptchaExecute(captchaToken =>
      authenticateWellevatePractitioner({
        variables: {
          input: {
            attributes: {
              email,
              password,
            },
            captchaToken,
          },
        },
      })
    );
  };

  const handleForgotPassword = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.WELLEVATE_MIGRATION_FORGOT_WELLEVATE_PASSWORD,
      payload: {},
    });

    window.open("https://akris-auth.emersonecologics.com/forgot-password?clientId=wvsso");
  };

  const handleSubmit = e => {
    e.preventDefault();
    handleWVSignIn();
  };

  const descriptiveText = (
    <Typography type="footnote">
      <Trans i18nKey={l.common.WellevateAuthenticationPage.passwordRequirements}>
        <FontAwesomeIcon icon={faInfoCircle} />
        {"  "}Must be 8 characters long
      </Trans>
    </Typography>
  );

  return (
    <>
      <Arrange justify="center">
        <BoxLink href="/">
          <FullscriptLogoSvg css={styles.fsLogo} />
          <Typography isSecondaryWeight isMarginless css={styles.fsSignInText}>
            {t(l.common.WellevateAuthenticationPage.fsSignIn)}
          </Typography>
          <FontAwesomeIcon
            icon={faArrowRight}
            size="lg"
            className="icon-right"
            css={styles.arrowIcon}
          />
        </BoxLink>
      </Arrange>
      <Spacer />
      <Arrange isFullwidth isVertical justify="start">
        <Arrange justify="center">
          <Typography type="h2" css={styles.textAlign}>
            {t(l.common.WellevateAuthenticationPage.title)}
          </Typography>
        </Arrange>
      </Arrange>

      <Spacer height="quad" />
      <div css={styles.boxContainer}>
        <NewBox padding="large" isBordered css={styles.box}>
          <Arrange isFullwidth isVertical justify="center" css={styles.boxArrange}>
            <div css={styles.textAlign}>
              <img
                src={WellevateByFullscript}
                alt={t(l.common.WellevateAuthenticationPage.wellevateByFullscript)}
                css={styles.wellevateByFullscriptLogo}
              />
            </div>

            <form css={styles.formStyle} onSubmit={handleSubmit} noValidate>
              <input type="submit" css={styles.noDisplay} />
              <EmailInput
                required
                hideSuggestion
                label={l.common.WellevateAuthenticationPage.wellevateEmail}
                value={email}
                setEmail={setEmail}
                onChange={handleEmailOnChange}
                isDirty={isEmailDirty}
                onBlur={handleEmailOnBlur}
              />

              <PasswordInput
                name="wellevatePassword"
                required
                label={t(l.common.WellevateAuthenticationPage.wellevatePassword)}
                onChange={handlePasswordOnChange}
                onBlur={handlePasswordOnBlur}
                value={password}
                errors={getPasswordErrors()}
                descriptiveText={descriptiveText}
                isDirty={isPasswordDirty}
                isLoading={authLoading}
              />

              <Button
                isFullWidth
                onClick={handleWVSignIn}
                css={styles.wellevateSignInButton}
                isLoading={authLoading}
              >
                {t(l.common.WellevateAuthenticationPage.signIn)}
              </Button>
            </form>

            <Typography type="footnote" sizeOverride="body" isSubdued css={styles.textAlign}>
              <Link onClick={handleForgotPassword} noExternalIcon>
                {t(l.common.WellevateAuthenticationPage.forgotPassword)}
              </Link>
            </Typography>
          </Arrange>
        </NewBox>
      </div>
    </>
  );
};

export { WellevateAuthenticationPage };
